import { Link } from "react-router-dom";
import { Microscope, FlaskConical, Leaf, ClipboardList } from "lucide-react";
import { useT, useLocalePath } from "@/i18n";

const icons = [Microscope, FlaskConical, Leaf, ClipboardList];

interface Pillar {
  num: string;
  title: string;
  tagline: string;
}

const PillarCard = ({ pillar, Icon }: { pillar: Pillar; Icon: typeof Microscope }) => (
  <article className="flex items-start gap-4 p-5 rounded-xl bg-card border border-border">
    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary shrink-0">
      <Icon className="w-5 h-5" />
    </span>
    <div>
      <span className="block text-xs font-semibold tracking-widest text-primary/70 mb-0.5">
        {pillar.num}
      </span>
      <h3 className="text-lg font-semibold text-foreground leading-snug">
        {pillar.title}
      </h3>
      <p className="text-sm text-muted-foreground mt-1 leading-snug">
        {pillar.tagline}
      </p>
    </div>
  </article>
);

const Saeulen = () => {
  const lp = useLocalePath();

  const t = useT(
    {
      eyebrow: "Mein Ansatz",
      heading: "Vier fachliche Perspektiven",
      pillars: [
        {
          num: "01",
          title: "Mikrobiom & Mikrobiologie",
          tagline: "Das Kleinste in uns – im Größten gesehen.",
        },
        {
          num: "02",
          title: "Biochemie & Mikronährstoffe",
          tagline: "Biochemische Vorgänge erkennen und verstehen.",
        },
        {
          num: "03",
          title: "Phytotherapie",
          tagline: "Pflanzliche Wirkstoffe richtig einsetzen.",
        },
        {
          num: "04",
          title: "Korrelative abklärende Analysen",
          tagline: "Zusammenhänge sehen und begreifen lernen.",
        },
      ],
      partnerTitle: "Kooperationen & spezialisierte Diagnostik",
      partnerPhageNum: "05",
      partnerPhageTitle: "Bakteriophagentherapie",
      partnerPhageText:
        "Begleitung bei schweren Infektionen in Kooperation mit dem Eliava Institut.",
      partnerPhageCta: "Mehr zur Bakteriophagentherapie",
      partnerMicroGenDxNum: "06",
      partnerMicroGenDxTitle: "MicroGenDx",
      partnerMicroGenDxText:
        "Molekulargenetische Diagnostik bei komplexen Infektionen, sofern nötig.",
      sketchTitle: "Vier Perspektiven – eine individuelle Betrachtung",
      sketchHub: "Individuelle\nPerspektive",
      sketchNodes: ["Mikrobiom", "Biochemie", "Phytotherapie", "Korrelative Analysen"],
    },
    {
      eyebrow: "My approach",
      heading: "Four professional perspectives",
      pillars: [
        {
          num: "01",
          title: "Microbiome & Microbiology",
          tagline: "Seeing the smallest within the greater whole.",
        },
        {
          num: "02",
          title: "Biochemistry & Micronutrients",
          tagline: "Recognising and understanding biochemical processes.",
        },
        {
          num: "03",
          title: "Phytotherapy",
          tagline: "Using plant-based remedies the right way.",
        },
        {
          num: "04",
          title: "Correlative clarifying analyses",
          tagline: "Learning to see how things connect.",
        },
      ],
      partnerTitle: "Cooperations & specialised diagnostics",
      partnerPhageNum: "05",
      partnerPhageTitle: "Bacteriophage therapy",
      partnerPhageText:
        "Support for severe infections in cooperation with the Eliava Institute.",
      partnerPhageCta: "More on bacteriophage therapy",
      partnerMicroGenDxNum: "06",
      partnerMicroGenDxTitle: "MicroGenDx",
      partnerMicroGenDxText:
        "Molecular diagnostics for complex infections, where necessary.",
      sketchTitle: "Four perspectives – an individual view",
      sketchHub: "Individual\nperspective",
      sketchNodes: ["Microbiome", "Biochemistry", "Phytotherapy", "Correlative analyses"],
    },
  );

  return (
    <section id="fachlicher-ansatz" className="py-12 md:py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-2">
            {t.eyebrow}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.heading}
          </h2>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {t.pillars.map((p, i) => {
            const Icon = icons[i];
            return <PillarCard key={p.num} pillar={p} Icon={Icon} />;
          })}
        </div>

        <div className="max-w-4xl mx-auto mt-10 md:mt-12">
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4 text-center">
            {t.partnerTitle}
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <article className="flex items-start gap-4 p-5 rounded-xl bg-card border border-border">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary shrink-0 text-xs font-semibold">
                {t.partnerPhageNum}
              </span>
              <div>
                <h4 className="text-lg font-semibold text-foreground leading-snug mb-1">
                  {t.partnerPhageTitle}
                </h4>
                <p className="text-sm text-muted-foreground leading-snug mb-2">
                  {t.partnerPhageText}
                </p>
                <Link
                  to={lp("/bakteriophagen-therapie")}
                  className="text-sm font-medium text-primary underline underline-offset-4 hover:no-underline"
                >
                  {t.partnerPhageCta}
                </Link>
              </div>
            </article>
            <article className="flex items-start gap-4 p-5 rounded-xl bg-card border border-border">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary shrink-0 text-xs font-semibold">
                {t.partnerMicroGenDxNum}
              </span>
              <div>
                <h4 className="text-lg font-semibold text-foreground leading-snug mb-1">
                  {t.partnerMicroGenDxTitle}
                </h4>
                <p className="text-sm text-muted-foreground leading-snug">
                  {t.partnerMicroGenDxText}
                </p>
              </div>
            </article>
          </div>
        </div>

        {/* Skizze: vier Perspektiven führen zur individuellen Betrachtung */}
        <div className="max-w-3xl mx-auto mt-10 md:mt-12 p-6 md:p-8 rounded-xl border border-primary/20 bg-primary/5 text-center">
          <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-6">
            {t.sketchTitle}
          </h3>
          <svg
            viewBox="0 0 400 400"
            role="img"
            aria-label={t.sketchTitle}
            className="mx-auto w-full max-w-[420px]"
          >
            <g className="stroke-primary/30" strokeWidth="1.5">
              <line x1="200" y1="60" x2="340" y2="200" />
              <line x1="340" y1="200" x2="200" y2="340" />
              <line x1="200" y1="340" x2="60" y2="200" />
              <line x1="60" y1="200" x2="200" y2="60" />
              <line x1="200" y1="60" x2="200" y2="340" />
              <line x1="60" y1="200" x2="340" y2="200" />
              <line x1="200" y1="60" x2="200" y2="200" />
              <line x1="340" y1="200" x2="200" y2="200" />
              <line x1="200" y1="340" x2="200" y2="200" />
              <line x1="60" y1="200" x2="200" y2="200" />
            </g>

            {[
              { x: 200, y: 60, label: t.sketchNodes[0] },
              { x: 340, y: 200, label: t.sketchNodes[1] },
              { x: 200, y: 340, label: t.sketchNodes[2] },
              { x: 60, y: 200, label: t.sketchNodes[3] },
            ].map((n) => {
              const words = n.label.split(" ");
              const lines =
                words.length > 2
                  ? [words.slice(0, 2).join(" "), words.slice(2).join(" ")]
                  : words.length === 2
                    ? [words[0], words[1]]
                    : [n.label];
              return (
                <g key={n.label}>
                  <circle
                    cx={n.x}
                    cy={n.y}
                    r="42"
                    className="fill-card stroke-primary/50"
                    strokeWidth="1.5"
                  />
                  <text
                    x={n.x}
                    y={n.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="fill-foreground"
                    fontSize="10"
                    fontWeight="600"
                  >
                    {lines.map((line, i) => (
                      <tspan
                        key={i}
                        x={n.x}
                        dy={i === 0 ? `${-(lines.length - 1) * 0.6}em` : "1.2em"}
                      >
                        {line}
                      </tspan>
                    ))}
                  </text>
                </g>
              );
            })}

            <circle
              cx="200"
              cy="200"
              r="58"
              className="fill-primary/10 stroke-primary"
              strokeWidth="2"
            />
            {t.sketchHub.split("\n").map((line, i, arr) => (
              <text
                key={line + i}
                x="200"
                y={200 + (i - (arr.length - 1) / 2) * 16}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-foreground"
                fontSize="13"
                fontWeight="600"
              >
                {line}
              </text>
            ))}
          </svg>
        </div>
      </div>
    </section>
  );
};

export default Saeulen;
